/**
 * Side cart upsell (Search & Discovery recommendations)
 *
 * Search & Discovery / the Recommendations API are product-level only: they cannot
 * return a size-matched variant. This script adds that layer client-side.
 *
 * Flow:
 *   1. Read the cart lines rendered by sections/side-cart.liquid and pick the MOST
 *      RECENTLY added line as the recommendation base (product id + Size value).
 *   2. Fetch recommendations for that product, rendered by
 *      sections/side-cart-upsells.liquid as hidden candidate tiles, each carrying its
 *      full available-variant matrix (size + color).
 *   3. For every candidate that has a Size option, preselect the variant whose size
 *      matches the base line's size. Candidates with no size match are dropped, and
 *      the next candidate takes the slot. Products with no Size option keep the
 *      previous behaviour (cheapest available variant).
 *
 * Size values are normalised through a built-in alias table, optionally extended by
 * the shop metafield custom.size_aliases (JSON: { "canonical": ["alias", ...] }).
 */

(() => {
  const LEGACY_STORAGE_KEY = 'we:sideCartUpsellBaseProductId';
  const LAST_ADDED_KEY = 'we:sideCartLastAddedVariantId';
  const DEBUG_KEY = 'we:debugSideCartUpsells';
  const UPSELL_SECTION_ID = 'side-cart-upsells';
  const DEFAULT_VISIBLE_LIMIT = 2;

  const DEFAULT_SIZE_ALIASES = {
    twin: ['twin'],
    'twin xl': ['twin xl', 'twinxl', 'twin x l', 'twin extra long'],
    full: ['full', 'double', 'full double'],
    queen: ['queen'],
    king: ['king', 'eastern king'],
    'cal king': ['cal king', 'calking', 'california king'],
  };

  /** Words that carry no size meaning and only create false mismatches. */
  const SIZE_NOISE_WORDS = ['size', 'bed', 'beds', 'mattress', 'mattresses', 'frame'];

  const debugEnabled = () => {
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const debugLog = (...args) => {
    if (!debugEnabled()) return;
    // eslint-disable-next-line no-console
    console.debug('[SideCartUpsells]', ...args);
  };

  const safeJsonParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };

  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const getSideCartElement = () => document.getElementById('sidebar-cart');
  const getUpsellContainer = (sideCartEl) => sideCartEl?.querySelector('[data-side-cart-upsells]');

  /**
   * Loading state: keep the heading and placeholder tiles visible while the
   * recommendations are (re)fetched, instead of collapsing the section. Markup comes
   * from the template rendered by sections/side-cart.liquid.
   */
  const showSkeleton = (containerEl, sideCartEl) => {
    if (containerEl.querySelector('[data-side-cart-upsells-skeleton-content]')) return;

    const template = sideCartEl?.querySelector('template[data-side-cart-upsells-skeleton]');
    if (template) containerEl.innerHTML = template.innerHTML;
  };

  const getSideCartSettings = (sideCartEl) => {
    if (!sideCartEl) return {};
    const raw = sideCartEl.getAttribute('data-section-settings') || '{}';
    return safeJsonParse(decodeHtmlEntities(raw), {});
  };

  const parseIdList = (csv) =>
    (csv || '')
      .split(',')
      .map((x) => parseInt(String(x).trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);

  /* ---------------------------------------------------------------------------
   * Size normalization
   * ------------------------------------------------------------------------ */

  const cleanSizeValue = (value) => {
    if (value === null || value === undefined) return '';
    const words = String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter((word) => word && !SIZE_NOISE_WORDS.includes(word));
    return words.join(' ');
  };

  let aliasLookup = null;

  const buildAliasLookup = (aliasesFromMetafield) => {
    const merged = { ...DEFAULT_SIZE_ALIASES };

    if (aliasesFromMetafield && typeof aliasesFromMetafield === 'object' && !Array.isArray(aliasesFromMetafield)) {
      Object.keys(aliasesFromMetafield).forEach((canonical) => {
        const value = aliasesFromMetafield[canonical];
        merged[canonical] = Array.isArray(value) ? value : [value];
      });
    }

    const lookup = {};
    Object.keys(merged).forEach((canonical) => {
      const canonicalKey = cleanSizeValue(canonical);
      if (canonicalKey) lookup[canonicalKey] = canonicalKey;
      (merged[canonical] || []).forEach((alias) => {
        const aliasKey = cleanSizeValue(alias);
        if (aliasKey) lookup[aliasKey] = canonicalKey || aliasKey;
      });
    });
    return lookup;
  };

  const setAliasLookupFrom = (contentEl) => {
    const raw = contentEl?.getAttribute('data-size-aliases') || '';
    const parsed = safeJsonParse(decodeHtmlEntities(raw), null);
    aliasLookup = buildAliasLookup(parsed);
    debugLog('size aliases loaded', aliasLookup);
  };

  const normalizeSize = (value) => {
    const cleaned = cleanSizeValue(value);
    if (!cleaned) return '';
    if (!aliasLookup) aliasLookup = buildAliasLookup(null);
    return aliasLookup[cleaned] || cleaned;
  };

  /* ---------------------------------------------------------------------------
   * Cart line context
   * ------------------------------------------------------------------------ */

  /**
   * Cart line order is NOT a reliable "most recently added" signal, so the add itself
   * is recorded instead. `product:added` is dispatched by every add path in this theme
   * (product form, quick add, Boost quick add, and the upsell tiles below), but its
   * payload is inconsistent: detail.variant is sometimes a variant object and sometimes
   * a bare variant id.
   */
  const readAddedVariantId = (detail) => {
    const raw = detail?.variant?.id ?? detail?.variant ?? detail?.variant_id ?? null;
    const id = parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const rememberLastAdded = (variantId) => {
    try {
      sessionStorage.setItem(LAST_ADDED_KEY, String(variantId));
    } catch (e) {}
  };

  const getLastAddedVariantId = () => {
    try {
      const id = parseInt(sessionStorage.getItem(LAST_ADDED_KEY) || '', 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch (e) {
      return null;
    }
  };

  const forgetLastAdded = () => {
    try {
      sessionStorage.removeItem(LAST_ADDED_KEY);
    } catch (e) {}
  };

  // Capture phase: run before the cart re-render triggers a reload of the upsells.
  document.addEventListener(
    'product:added',
    (e) => {
      const variantId = readAddedVariantId(e?.detail);
      if (variantId) {
        rememberLastAdded(variantId);
        debugLog('recorded last added variant', variantId);
      }
    },
    true
  );

  /**
   * Resolve the cart line the recommendations should be based on.
   *
   * Priority:
   *   1. The line matching the variant recorded from the last `product:added`.
   *   2. First line in cart order — Shopify's AJAX cart puts the newest line first,
   *      but this is only a fallback because that order is not contractual.
   *   3. Legacy data attributes (no size context).
   */
  const getBaseLine = (containerEl) => {
    const lines = safeJsonParse(decodeHtmlEntities(containerEl.getAttribute('data-cart-lines') || ''), null);

    if (Array.isArray(lines) && lines.length > 0) {
      const lastAddedVariantId = getLastAddedVariantId();

      if (lastAddedVariantId) {
        const match = lines.find((line) => parseInt(line?.variant_id, 10) === lastAddedVariantId);
        if (match) {
          const productId = parseInt(match.product_id, 10);
          if (Number.isFinite(productId)) {
            debugLog('base line from last added variant', { lastAddedVariantId, size: match.size });
            return { productId, size: match.size || '' };
          }
        } else {
          forgetLastAdded();
        }
      }

      const fallback = lines[0];
      const fallbackProductId = parseInt(fallback?.product_id, 10);
      if (Number.isFinite(fallbackProductId)) {
        debugLog('base line from cart order fallback', { size: fallback?.size });
        return { productId: fallbackProductId, size: fallback?.size || '' };
      }
    }

    const cartProductIds = parseIdList(containerEl.dataset.cartProductIds);
    const legacyId =
      parseInt(containerEl.dataset.lastCartProductId || '', 10) ||
      parseInt(containerEl.dataset.firstCartProductId || '', 10) ||
      cartProductIds[0];

    return Number.isFinite(legacyId) ? { productId: legacyId, size: '' } : null;
  };

  /* ---------------------------------------------------------------------------
   * Tile rendering
   * ------------------------------------------------------------------------ */

  const formatUpsellPriceHtml = (variant) => {
    const now = variant?.price_formatted ?? '';
    const was = variant?.compare_at_price_formatted ?? '';
    const hasSale = Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);

    if (hasSale && was) {
      return `<span class="SideCartUpsellTile__PriceNow">${now}</span><span class="SideCartUpsellTile__PriceWas">${was}</span>`;
    }
    return `<span class="SideCartUpsellTile__PriceNow">${now}</span>`;
  };

  const cheapest = (variants) => variants.reduce((best, v) => (best === null || Number(v.price) < Number(best.price) ? v : best), null);

  const uniqueValues = (variants, key) => {
    const seen = [];
    variants.forEach((v) => {
      const value = v?.[key];
      if (value && !seen.includes(value)) seen.push(value);
    });
    return seen;
  };

  const getTileVariants = (tileEl) => {
    const parsed = safeJsonParse(decodeHtmlEntities(tileEl.getAttribute('data-upsell-variants') || '[]'), []);
    return Array.isArray(parsed) ? parsed.filter((v) => v && v.available !== false) : [];
  };

  /**
   * Decide whether a candidate tile can be shown, and with which size preselected.
   * Returns null when the tile has a Size option but no variant matching the base size
   * (UC-5 / UC-6: never silently fall back to the cheapest size).
   */
  const resolveTile = (tileEl, baseSize) => {
    const variants = getTileVariants(tileEl);
    if (variants.length === 0) return null;

    const productHasSize = tileEl.dataset.upsellHasSize === 'true' && variants.some((v) => v.size);
    const normalizedBase = normalizeSize(baseSize);

    if (!productHasSize || !normalizedBase) {
      return { variants, size: cheapest(variants)?.size || '' };
    }

    const matched = variants.filter((v) => normalizeSize(v.size) === normalizedBase);
    if (matched.length === 0) {
      debugLog('no size match, dropping tile', {
        productId: tileEl.dataset.upsellProductId,
        baseSize,
        sizes: uniqueValues(variants, 'size'),
      });
      return null;
    }

    return { variants, size: matched[0].size };
  };

  /**
   * Wire a dropdown whose options are already rendered by
   * sections/side-cart-upsells.liquid (see the note there: JS must not create the
   * option elements, or Tailwind purges their class). Options outside
   * `allowedValues` are hidden, and the whole control is hidden when it offers no
   * real choice.
   */
  const initDropdown = (dropdownEl, allowedValues, selectedValue, onSelect) => {
    if (!dropdownEl) return;

    const toggle = dropdownEl.querySelector('[data-upsell-option-toggle]');
    const valueEl = dropdownEl.querySelector('[data-upsell-option-value]');
    const menu = dropdownEl.querySelector('[data-upsell-option-menu]');
    if (!toggle || !valueEl || !menu) return;

    dropdownEl._upsellOnSelect = onSelect;

    let visibleCount = 0;

    menu.querySelectorAll('[data-upsell-option-label]').forEach((optionEl) => {
      const label = optionEl.getAttribute('data-upsell-option-label') || '';
      const allowed = allowedValues.includes(label);

      optionEl.hidden = !allowed;
      optionEl.setAttribute('aria-selected', allowed && label === selectedValue ? 'true' : 'false');
      if (allowed) visibleCount += 1;

      if (!optionEl.dataset.upsellBound) {
        optionEl.dataset.upsellBound = '1';
        optionEl.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          valueEl.textContent = label;
          menu.querySelectorAll('[data-upsell-option-label]').forEach((x) => x.setAttribute('aria-selected', 'false'));
          optionEl.setAttribute('aria-selected', 'true');
          dropdownEl.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          dropdownEl._upsellOnSelect?.(label);
        });
      }
    });

    valueEl.textContent = selectedValue || allowedValues[0] || '';

    dropdownEl.hidden = visibleCount <= 1;

    if (!toggle.dataset.upsellBound) {
      toggle.dataset.upsellBound = '1';
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = dropdownEl.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }
  };

  const initUpsellTile = (tileEl, resolution) => {
    const variants = resolution.variants;

    const sizeDropdown = tileEl.querySelector('[data-upsell-dropdown-type="size"]');
    const colorDropdown = tileEl.querySelector('[data-upsell-dropdown-type="color"]');
    const priceEl = tileEl.querySelector('[data-upsell-price]');
    const imageEl = tileEl.querySelector('[data-upsell-image]');
    const addBtn = tileEl.querySelector('[data-upsell-add]');

    const sizes = uniqueValues(variants, 'size');
    const state = {
      size: resolution.size || sizes[0] || '',
      color: '',
    };

    const variantsForSize = (size) => (size ? variants.filter((v) => v.size === size) : variants.slice());

    const pickVariant = () => {
      const pool = variantsForSize(state.size);
      const withColor = state.color ? pool.filter((v) => v.color === state.color) : pool;
      return cheapest(withColor.length > 0 ? withColor : pool) || cheapest(variants);
    };

    const applySelection = () => {
      const variant = pickVariant();
      if (!variant) return;

      tileEl.dataset.selectedVariantId = String(variant.id);
      tileEl.dataset.selectedItemStatus = variant.item_status ? String(variant.item_status) : '';
      if (priceEl) priceEl.innerHTML = formatUpsellPriceHtml(variant);
      if (addBtn) addBtn.disabled = variant.available === false;
      if (imageEl && variant.image_url) {
        imageEl.src = variant.image_url;
        if (imageEl.hasAttribute('srcset')) imageEl.removeAttribute('srcset');
      }
    };

    const renderColors = () => {
      const colors = uniqueValues(variantsForSize(state.size), 'color');
      if (!state.color || !colors.includes(state.color)) {
        state.color = cheapest(variantsForSize(state.size))?.color || colors[0] || '';
      }
      initDropdown(colorDropdown, colors, state.color, (value) => {
        state.color = value;
        applySelection();
      });
    };

    initDropdown(sizeDropdown, sizes, state.size, (value) => {
      state.size = value;
      renderColors();
      applySelection();
    });

    renderColors();
    applySelection();

    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const selectedVariantId = parseInt(tileEl.dataset.selectedVariantId || '', 10);
        if (!Number.isFinite(selectedVariantId)) return;

        const itemStatus = (tileEl.dataset.selectedItemStatus || '').trim();
        const vendor = tileEl.dataset.vendor || '';
        const properties = itemStatus
          ? {
              'Item Status': itemStatus,
              _vendor: vendor,
              _purchased_by_upsell: 'cart_upsells',
            }
          : {
              _vendor: vendor,
              _purchased_by_upsell: 'cart_upsells',
            };

        addBtn.disabled = true;
        tileEl.classList.add('is-adding');

        try {
          const response = await fetch(`${window.routes.cartAddUrl}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: selectedVariantId, quantity: 1, ...(properties ? { properties } : {}) }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => null);
            console.error('Side cart upsell add failed', err || response.status);
            return;
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { variant: { id: selectedVariantId }, quantity: 1 },
            })
          );
        } catch (e) {
          console.error('Side cart upsell add error', e);
        } finally {
          tileEl.classList.remove('is-adding');
          addBtn.disabled = false;
        }
      });
    }
  };

  /**
   * Reveal the first `limit` candidates that resolve to a size match; drop the rest.
   */
  const initUpsells = (containerEl, baseSize) => {
    const contentEl = containerEl.querySelector('[data-side-cart-upsells-content]');
    const limit = parseInt(contentEl?.dataset.upsellVisibleLimit || '', 10) || DEFAULT_VISIBLE_LIMIT;

    let shown = 0;

    containerEl.querySelectorAll('[data-side-cart-upsell-tile]').forEach((tileEl) => {
      if (shown >= limit) {
        tileEl.remove();
        return;
      }

      const resolution = resolveTile(tileEl, baseSize);
      if (!resolution) {
        tileEl.remove();
        return;
      }

      initUpsellTile(tileEl, resolution);
      tileEl.hidden = false;
      tileEl.style.removeProperty('display');
      shown += 1;
    });

    if (shown === 0) {
      debugLog('no eligible upsell tiles', { baseSize });
      containerEl.innerHTML = '';
    }
  };

  const loadSideCartUpsells = async () => {
    const sideCartEl = getSideCartElement();
    if (!sideCartEl) return;

    const containerEl = getUpsellContainer(sideCartEl);
    if (!containerEl) return;

    const settings = getSideCartSettings(sideCartEl);
    if (settings.upsellEnabled === false) {
      containerEl.innerHTML = '';
      return;
    }

    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (e) {}

    if (parseIdList(containerEl.dataset.cartProductIds).length === 0) {
      forgetLastAdded();
      containerEl.innerHTML = '';
      return;
    }

    showSkeleton(containerEl, sideCartEl);

    const baseLine = getBaseLine(containerEl);
    if (!baseLine) {
      debugLog('no base cart line');
      containerEl.innerHTML = '';
      return;
    }

    const intent = settings.upsellIntent === 'complementary' ? 'complementary' : 'related';
    const baseQuery =
      `${window.routes.productRecommendationsUrl}` +
      `?section_id=${encodeURIComponent(UPSELL_SECTION_ID)}` +
      `&product_id=${encodeURIComponent(String(baseLine.productId))}` +
      `&limit=10` +
      `&timestamp=${Date.now()}`;
    const urlWithIntent = `${baseQuery}&intent=${encodeURIComponent(intent)}`;
    const urlNoIntent = baseQuery;

    try {
      const fetchAndExtract = async (url) => {
        debugLog('fetching', { url, baseLine, intent });
        const response = await fetch(url, { credentials: 'same-origin', method: 'GET' });
        const html = await response.text();
        debugLog('response', { status: response.status, length: html.length });
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return { html, temp };
      };

      let { html, temp } = await fetchAndExtract(urlWithIntent);

      let contentEl = temp.querySelector('[data-side-cart-upsells-content]');
      if (!contentEl) {
        debugLog('no content with intent, retrying without intent');
        ({ html, temp } = await fetchAndExtract(urlNoIntent));
        contentEl = temp.querySelector('[data-side-cart-upsells-content]');
      }

      if (!contentEl) {
        if (html.includes('Liquid error') || html.includes('Liquid syntax error')) {
          console.error('Side cart upsell Liquid error in response:', html);
        }
        containerEl.innerHTML = '';
        return;
      }

      setAliasLookupFrom(contentEl);

      containerEl.innerHTML = contentEl.outerHTML;
      initUpsells(containerEl, baseLine.size);
    } catch (e) {
      console.error('Side cart upsell load error', e);
      containerEl.innerHTML = '';
    }
  };

  document.addEventListener('cart:content-replaced', () => loadSideCartUpsells());

  document.addEventListener('click', (e) => {
    const trigger = e.target?.closest?.('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
    if (!trigger) return;
    setTimeout(() => loadSideCartUpsells(), 0);
  });

  // Close any open upsell dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    const inside = e.target?.closest?.('[data-upsell-dropdown]');
    if (inside) return;
    document.querySelectorAll('[data-upsell-dropdown].is-open').forEach((el) => {
      el.classList.remove('is-open');
      const toggle = el.querySelector('[data-upsell-option-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('DOMContentLoaded', () => loadSideCartUpsells());
})();
